//! userAssets & userCategories can actually be queried in a single call via the user-settings endpoint of the Notification API.
//! However, for UI responsiveness, the tables for Categories & Assets are populated through separate (albeit redundant) GET requests.
//! Similarly, when saving user settings, separate POST requests are made for userCategories & userAssets, although atm backend does not accept posting userAssets only.
//! In the future™️, the generic user-settings endpoints (GET/POST) could/should(?) be replaced by separate endpoints for userCategories (GET/POST) & userAssets (GET/POST).

export const vNotificationAPI = "2.0";
export const vAssetManagementApi = "1.0";

export type Stage = "INT" | "QA" | "PROD";

export type RawCategory = {
	id: string;
	locale: string;
	name: string;
	title: string;
	businessCritical: boolean;
	tooltip: string;
};

export type UserCategory = {
	name: string;
	channels: {name: string; enabled: boolean}[];
};

export type RawAsset = {
	assetId: string;
	assetName: string;
};

export type UserAsset = {
	id: string;
	enabled: boolean;
};

const getEndPoint = ({stage, api, version}: {stage: Stage; api: "assetManagement" | "notification"; version: string}) => {
	const endPoints = {
		INT: {
			assetManagement: `https://api-s.trumpf.com/asset-management/${version}/core/assets`,
			notification: `https://notifications-d.my.trumpf.com/${version}`,
		},
		QA: {
			assetManagement: `https://api-s.trumpf.com/asset-management/${version}/core/assets`,
			notification: `https://notifications-q.my.trumpf.com/${version}`,
		},
		PROD: {
			assetManagement: `https://api-p.trumpf.com/asset-management/${version}/core/assets`,
			notification: `https://notifications.my.trumpf.com/${version}`,
		},
	};

	return endPoints[stage][api];
};

const fetchOptions = ({
	method,
	contentType = "application/json",
	tokenType,
	token,
}: {
	method: "GET" | "POST" | "PUT" | "DELETE";
	contentType?: string;
	tokenType: "Bearer" | "X-JWT-Assertion";
	token: string;
}): RequestInit => {
	if (!token) throw new Error("A non-empty token is required.");

	const authHeaders = tokenType === "Bearer" ? {Authorization: `Bearer ${token}`} : {"X-JWT-Assertion": token};

	return {
		method,
		headers: new Headers({
			"Content-Type": contentType,
			...authHeaders,
		}),
	};
};

type CommonArgs = {stage: Stage; token: string};

export const getRawCategories = async ({stage, token, locale}: CommonArgs & {locale: string}) => {
	const options = fetchOptions({method: "GET", tokenType: "X-JWT-Assertion", token});
	const url = new URL(`${getEndPoint({stage, api: "notification", version: vNotificationAPI})}/categories`);
	url.searchParams.append("locale", locale);

	const response = await fetch(url, options);
	if (!response.ok) throw new Error(`Failed to fetch rawCategories: ${response.statusText}`);

	const data = (await response.json()) as {categories: RawCategory[]};
	return data.categories;
};

export const getUserCategories = async ({stage, token}: CommonArgs) => {
	const options = fetchOptions({method: "GET", tokenType: "X-JWT-Assertion", token});
	const url = new URL(`${getEndPoint({stage, api: "notification", version: vNotificationAPI})}/user-settings`);

	const response = await fetch(url, options);
	if (!response.ok) throw new Error(`Failed to fetch userCategories: ${response.statusText}`);

	const data = (await response.json()) as {categories: UserCategory[]};
	return data.categories;
};

//! Currently, backend does not allow to only update userCategories, so we need to send all userSettings.
//! Replace with function below once the backend is ready.
export const postLegacyUserCategories = async ({
	stage,
	token,
	legacySettings,
}: CommonArgs & {legacySettings: {categories: UserCategory[]; assets: UserAsset[]}}) => {
	const options = fetchOptions({method: "POST", tokenType: "X-JWT-Assertion", token});
	const url = new URL(`${getEndPoint({stage, api: "notification", version: vNotificationAPI})}/user-settings`);

	const response = await fetch(url, {...options, body: JSON.stringify(legacySettings)});
	if (!response.ok) throw new Error(`Failed to post userCategories: ${response.statusText}`);
};

//! Request against a separate userCategories endpoint that is yet to be implemented
const postUserCategories = async ({stage, token, categories}: CommonArgs & {categories: UserCategory[]}) => {
	console.error("DO NOT USE ME YET");
	return;

	const options = fetchOptions({method: "POST", tokenType: "X-JWT-Assertion", token});
	const url = new URL(`${getEndPoint({stage, api: "notification", version: vNotificationAPI})}/user-settings`); // replace with userCategories endpoint

	const response = await fetch(url, {...options, body: JSON.stringify({categories})});
	if (!response.ok) throw new Error(`Failed to post userCategories: ${response.statusText}`);
};

export const getRawAssets = async ({stage, token}: CommonArgs) => {
	const rawAssets: RawAsset[] = [];
	let totalCount = Infinity;
	let offset = 0;

	const options = fetchOptions({method: "GET", tokenType: "Bearer", token});
	const baseUrl = new URL(getEndPoint({stage, api: "assetManagement", version: vAssetManagementApi}));
	baseUrl.searchParams.append("isRootAsset", "true");
	baseUrl.searchParams.append("limit", "100"); // max provided by api

	while (rawAssets.length < totalCount) {
		const tmpUrl = new URL(baseUrl);
		tmpUrl.searchParams.append("offset", offset.toString());

		const response = await fetch(tmpUrl, options);
		if (!response.ok) throw new Error(`Failed to fetch rawAssets: ${response.statusText}`);

		const data = (await response.json()) as {totalCount: number; results: (RawAsset & Record<string, unknown>)[]};
		rawAssets.push(...data.results);

		if (totalCount === Infinity) totalCount = data.totalCount; // update totalCount on first fetch
		offset += data.results.length;
	}

	return rawAssets;
};

export const getUserAssets = async ({stage, token}: CommonArgs) => {
	const options = fetchOptions({method: "GET", tokenType: "X-JWT-Assertion", token});
	const url = new URL(`${getEndPoint({stage, api: "notification", version: vNotificationAPI})}/user-settings`);

	const response = await fetch(url, options);
	if (!response.ok) throw new Error(`Failed to fetch userAssets: ${response.statusText}`);

	const data = (await response.json()) as {assets: UserAsset[]};
	return data.assets;
};

//! Currently, backend does not allow to only update userAssets, so we need to send all userSettings.
//! Replace with function below once the backend is ready.
export const postLegacyUserAssets = async ({
	stage,
	token,
	legacySettings,
}: CommonArgs & {legacySettings: {categories: UserCategory[]; assets: UserAsset[]}}) => {
	const options = fetchOptions({method: "POST", tokenType: "X-JWT-Assertion", token});
	const url = new URL(`${getEndPoint({stage, api: "notification", version: vNotificationAPI})}/user-settings`);

	const response = await fetch(url, {...options, body: JSON.stringify(legacySettings)});
	if (!response.ok) throw new Error(`Failed to post userAssets: ${response.statusText}`);
};

//! Request against a separate userAssets endpoint that is yet to be implemented
const postUserAssets = async ({stage, token, assets}: CommonArgs & {assets: UserAsset[]}) => {
	console.error("DO NOT USE ME YET");
	return;

	const options = fetchOptions({method: "POST", tokenType: "X-JWT-Assertion", token});
	const url = new URL(`${getEndPoint({stage, api: "notification", version: vNotificationAPI})}/user-settings`); // replace with userAssets endpoint

	const response = await fetch(url, {...options, body: JSON.stringify({assets})});
	if (!response.ok) throw new Error(`Failed to post userAssets: ${response.statusText}`);
};
